/*
 *  -----------------------------------------------------------------------
 *    <copyright file="system-users-grid.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import { orderBy } from '@progress/kendo-data-query';
import React, { useEffect, useState } from 'react';
import { useTranslation, useGridCells } from 'hooks';
import { OrganisationMemberStatus } from 'types/graphql-global-types';
import { restructuredUpdatedCreatedBy } from '@utils-clean/update-users-array';
import { getAdminLevelName } from 'views/components/admin/users/system-users-service';
import ExpandableGrid from 'views/components/core/expandable/grid';
import { GridColumnMenuActiveFilter } from 'views/components/core/grid-filters';
import { stringDateWithoutTimeFromCurrentUtcDate } from '@utils/convertor';

import './styles.scss';

const SystemUsersGrid = ({ items, actions }: any) => {
    const { t } = useTranslation();
    const { YesNoCell, DateCell, MaxLengthCell, StandardCell, DateTimeCell } = useGridCells();

    const [data, setData] = useState([]);

    useEffect(() => {
        let array = [...items].map((item: any) => {
            const status =
                item.status === OrganisationMemberStatus.VERIFYING || !item.lastLoginTime
                    ? 'Invited'
                    : item.status === OrganisationMemberStatus.JOINED
                      ? 'Verified'
                      : item.status;
            return {
                ...item,
                _systemAdminLevel: getAdminLevelName(item.systemAdminLevel),
                _status: status,
                _statusOrder: status === 'Invited' ? 3 : status === 'Verified' ? 1 : 2,
            };
        });
        const newData = restructuredUpdatedCreatedBy(array);
        array = orderBy(newData, [
            { field: '_statusOrder', dir: 'asc' },
            { field: 'lastName', dir: 'asc' },
        ]);
        setData(array);
    }, [items]);

    const exportFileName = `OPRA System Users_${stringDateWithoutTimeFromCurrentUtcDate()}`;

    return (
        <ExpandableGrid
            customKey="data-users-grid"
            items={data}
            showPager
            groupable={false}
            rowActions={actions}
            scrollable="scrollable"
            reorderable
            pageable={false}
            resizable
            addScroll
            excelExportOptions={{
                fileName: exportFileName,
                name: exportFileName,
            }}
            options={{
                allowExcelExport: true,
                allowExpandCollapseAll: false,
                useFilterChips: false,
            }}
            detail={null}
            columns={[
                {
                    field: 'lastName',
                    id: 'user-last-name',
                    label: t('lastName'),
                    minWidth: 180,
                    lockedByDefault: true,
                },
                {
                    field: 'firstName',
                    id: 'user-first-name',
                    label: t('firstName'),
                    minWidth: 180,
                },
                {
                    field: 'email',
                    id: 'user-email',
                    label: t('email'),
                    minWidth: 250,
                    cells: { data: MaxLengthCell(150) },
                },
                {
                    field: '_systemAdminLevel',
                    id: 'user-systemAdminLevel',
                    label: t('level'),
                    minWidth: 250,
                },
                {
                    field: '_status',
                    id: 'user-status',
                    label: t('ra:status'),
                    minWidth: 250,
                    cells: { data: StandardCell(true) },
                },
                {
                    label: t('active'),
                    id: 'uer-active',
                    field: 'isEnabled',
                    minWidth: 130,
                    cells: { data: YesNoCell },
                    filterUi: GridColumnMenuActiveFilter,
                },
                {
                    label: t('ra:last-login'),
                    id: 'uer-lastLogin',
                    field: 'lastLoginTime',
                    minWidth: 200,
                    cells: { data: DateTimeCell('DD-MMM-YYYY HH:mm:ss UTC') },
                    filter: 'date',
                },
                {
                    label: t('ra:createdBy'),
                    field: '_createdByString',
                    id: 'user-createdby',
                    minWidth: 200,
                },
                {
                    label: t('ra:createdDate'),
                    field: 'created',
                    id: 'user-created',
                    minWidth: 200,
                    cells: { data: DateCell },
                    filter: 'date',
                },
                {
                    label: t('ra:updatedBy'),
                    field: '_updatedByString',
                    id: 'user-updatedby',
                    minWidth: 200,
                },
                {
                    label: t('ra:updatedDate'),
                    field: 'updated',
                    id: 'user-updated',
                    minWidth: 200,
                    cells: { data: DateCell },
                    filter: 'date',
                },
            ]}
        />
    );
};

export default SystemUsersGrid;
